"use client"

import { useEffect, useState } from "react"

// One client hook the careers marketing surfaces use to render pricing, so the
// $9.95/month standard price, its free-trial length, and the (dormant) $5.99/week launch
// special are decided in exactly one place (GET /api/career/pricing, itself backed by the
// career_settings promo flag). Starts from the no-promo default ($9.95/month), so there is
// never a flash of a wrong promo state before the fetch resolves, and it degrades to
// $9.95/month if the fetch fails.
type PriceLine = { cents: number; label: string; interval: "week" | "month" }
export type CareerPricingView = {
  promoActive: boolean
  promoName: string
  trialDays: number
  standard: PriceLine
  launch: PriceLine
  effective: PriceLine
  // Real savings vs the standard price (for honest "Save $X / N% off" emphasis when the promo is on).
  savings: { perWeekLabel: string; pct: number }
}

function savingsOf(standardCents: number, launchCents: number): { perWeekLabel: string; pct: number } {
  const diff = Math.max(0, standardCents - launchCents)
  return { perWeekLabel: `$${(diff / 100).toFixed(2)}`, pct: standardCents > 0 ? Math.round((diff / standardCents) * 100) : 0 }
}

export const CAREER_PRICING_DEFAULT: CareerPricingView = {
  promoActive: false,
  promoName: "Launch Special",
  trialDays: 7,
  standard: { cents: 995, label: "$9.95", interval: "month" },
  launch: { cents: 599, label: "$5.99", interval: "week" },
  effective: { cents: 995, label: "$9.95", interval: "month" },
  savings: savingsOf(995, 599),
}

export function useCareerPricing(): CareerPricingView {
  const [pricing, setPricing] = useState<CareerPricingView>(CAREER_PRICING_DEFAULT)
  useEffect(() => {
    let active = true
    fetch("/api/career/pricing")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!active || !j || !j.ok) return
        setPricing({
          promoActive: Boolean(j.promo_active),
          promoName: j.promo_name || "Launch Special",
          trialDays: Number.isFinite(j.trial_days) ? j.trial_days : 7,
          standard: j.standard,
          launch: j.launch,
          effective: j.effective,
          savings: savingsOf(j.standard?.cents ?? 995, j.launch?.cents ?? 599),
        })
      })
      .catch(() => {})
    return () => { active = false }
  }, [])
  return pricing
}
